import { FC, useEffect, useRef, useState, MouseEvent } from "react";
import { useNavigate } from "react-router-dom";

import { useAppSelector } from "redux/hooks";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "context/media/MediaContext";

import useOutsideClick from "helpers/hooks/useOutsideClick";
import {
  formattingUserName,
  initialsFromName,
  maskedString,
  maxName,
} from "utils/textfield.utils";

import {
  StyledDesktopRightMenu,
  StyledImageField,
  StyledWelcomeField,
  StyledDropdown,
  StyledRefreshMerchantWrapper,
  StyledContentContainer,
  StyledText,
  StyledSubMenu,
} from "./ProfileMenu.styles";
import Image, { ICONS } from "components/BaseComponents/Image";
import Typography from "components/BaseComponents/Typography";
import LogoutModal from "components/Auth/LogoutModal";

const ProfileMenu: FC = () => {
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState<boolean>(false);
  const [merchantName, setMerchantName] = useState("");
  const [isDropDownOpen, setIsDropDownOpen] = useState<boolean>(false);
  const dropDownRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery();

  const { user } = useAppSelector((state) => state.login);
  const { userOtp } = useAppSelector((state) => state.otp);
  const { merchant, selectedMerchant } = useAppSelector(
    (state) => state.merchant
  );

  const userName: string = user?.adSoyad!;

  useOutsideClick(dropDownRef, setIsDropDownOpen);

  const handleLogoutClick = (event: MouseEvent): void => {
    setIsDropDownOpen((prevState) => !prevState);
    event.stopPropagation();
    setIsLogoutModalOpen((prevState) => !prevState);
  };

  useEffect(() => {
    merchant?.Merchants?.filter(
      (f: any) => f.No === selectedMerchant && setMerchantName(f.Name)
    );
  }, [merchant?.Merchants, selectedMerchant]);

  return (
    <>
      <StyledDesktopRightMenu>
        <Image
          alt='Avatar Icon'
          height='28px'
          onClick={() => setIsDropDownOpen(!isDropDownOpen)}
          srcSet={ICONS.VERTICAL_DOTS}
          tabIndex={0}
          width='24px'
        />

        <StyledImageField>
          {maxName(formattingUserName(initialsFromName(userName)))}
        </StyledImageField>

        {isDropDownOpen && (
          <StyledDropdown
            className='dropdown'
            ref={dropDownRef}
            onClick={() => setIsDropDownOpen(!isDropDownOpen)}
          >
            <StyledWelcomeField>
              <Typography display='flex' size='14px'>
                {t("avatar_hello")}
              </Typography>

              <Typography margin='5px 0' size='12px' weight='500'>
                {maskedString(userName)}
              </Typography>

              <Typography
                display='flex'
                margin='5px 0'
                size='10px'
                weight='500'
              >
                {t("otp_last_login_date")}
                {userOtp?.lastVisitDate} {userOtp?.lastVisitTime}
              </Typography>
              {merchantName && (
                <Typography margin='5px 0' size='10px' weight='1000'>
                  {t("avatar_merchant_name")}
                  {merchantName}
                </Typography>
              )}
              {selectedMerchant && (
                <StyledRefreshMerchantWrapper>
                  <StyledContentContainer>
                    <StyledText>{t("avatar_merchant_number")}</StyledText>

                    <Typography
                      display='flex'
                      margin='0 0 0 3px'
                      size='10px'
                      variant='span'
                      weight='500'
                      width='auto'
                    >
                      {selectedMerchant}
                    </Typography>
                  </StyledContentContainer>
                </StyledRefreshMerchantWrapper>
              )}
            </StyledWelcomeField>

            <StyledSubMenu
              onClick={() =>
                navigate(`/my-profile/${merchantName || userName}`)
              }
            >
              <Image
                alt='Menu Icon'
                height={isMobile ? "24px" : "32px"}
                srcSet={ICONS.PROFILE}
                width={isMobile ? "24px" : "32px"}
              />
              {t("avatar_profile")}
            </StyledSubMenu>

            {userOtp?.UserLevel === "2" && (
              <StyledSubMenu
                onClick={() =>
                  navigate("/get-password", {
                    state: {
                      fromEmpDetail: true,
                    },
                  })
                }
              >
                <Image
                  alt='Menu Icon'
                  height={isMobile ? "24px" : "32px"}
                  srcSet={ICONS.PASSWORD_SECURITY}
                  width={isMobile ? "24px" : "32px"}
                />
                {t("password_refresh")}
              </StyledSubMenu>
            )}

            <StyledSubMenu onClick={() => navigate("/faq-list")}>
              <Image
                alt='Menu Icon'
                height={isMobile ? "24px" : "32px"}
                srcSet={ICONS.QUESTION_MARK}
                width={isMobile ? "24px" : "32px"}
              />
              {t("avatar_questions")}
            </StyledSubMenu>

            <StyledSubMenu onClick={(event) => handleLogoutClick(event)}>
              <Image
                alt='Logout'
                height='32px'
                srcSet={ICONS.LOGOUT}
                width='32px'
              />
              {t("logout")}
            </StyledSubMenu>
          </StyledDropdown>
        )}
      </StyledDesktopRightMenu>

      {isLogoutModalOpen && (
        <LogoutModal
          acceptButtonText={t("yes").toString()}
          message={t("are_you_sure_logout").toString()}
          rejectButtonText={t("no").toString()}
          setIsOpen={setIsLogoutModalOpen}
          title={t("logout").toString()}
        />
      )}
    </>
  );
};

export default ProfileMenu;
